import React from 'react';
import { Mail, ArrowUpRight, ShieldCheck, Scale, Ear, RefreshCw } from 'lucide-react';
import PageHero from '../components/PageHero';
import { COMPANY } from '../data/content';

const CULTURE = [
  { icon: ShieldCheck, t: 'Trust & Dignity', b: 'Team members are encouraged and empowered to do what is RIGHTEOUS — even above and beyond the SOPs laid out to ANTICIPATE, MEET & EXCEED the SERVICE expectations of the CUSTOMER, especially in the time of NEED.' },
  { icon: Scale, t: 'Fairness', b: 'Gender, Age, Community Inclusiveness, Unity in Diversity, Treating Humans as Humans and the Courage to Make and Accept Mistakes — call a Spade a Spade as well.' },
  { icon: Ear, t: 'Listening', b: 'Being humble and attentive enough to recognise and hunt out the BEST idea in the room — irrespective of WHO gives that IDEA.' },
  { icon: RefreshCw, t: 'Change', b: "Changing, Improving, Evolving — because we believe that Company's Vision, Mission and Values will lead to achievement of both individual and collective Competitive Greatness Together." },
];

export default function Careers() {
  return (
    <main>
      <PageHero eyebrow="Join the Team" title="Build your career" accent="at the energy frontier." desc="We're always interested in hearing from engineers, project managers and energy professionals who want to shape India's electrical and solar infrastructure." />

      {/* Team banner */}
      <section className="px-6 md:px-10 lg:px-14 -mt-2 pb-4 bg-[var(--cream)]">
        <div className="max-w-[1500px] mx-auto reveal">
          <div className="relative rounded-3xl overflow-hidden border border-[var(--line)] group">
            <img src="/img/careers.jpg" alt="Tricolite team" className="w-full h-[300px] md:h-[460px] object-cover transition-transform duration-1000 group-hover:scale-[1.03]"/>
            <div className="absolute inset-0 bg-gradient-to-tr from-[var(--ink)]/70 via-transparent to-transparent"/>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-2">The Team</div>
                <div className="font-display font-bold text-[24px] md:text-[36px] leading-tight" style={{color:'#fff'}}>People who power Tricolite Energy.</div>
              </div>
              <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[12px] uppercase tracking-wider text-white">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--orange)]"/> Engineers · Project Managers · Site Crews
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)]">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-14 max-w-3xl reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Our Culture</div>
            <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">Why <span className="font-serif-it text-[var(--orange)]">Tricolite Energy?</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {CULTURE.map((c, i) => (
              <div key={i} className="group bg-white border border-[var(--line)] rounded-2xl p-8 hover-lift hover:border-[var(--orange)]/40 reveal" style={{ transitionDelay: `${i*60}ms` }}>
                <div className="w-14 h-14 rounded-xl bg-[var(--orange)]/10 flex items-center justify-center mb-6 group-hover:bg-[var(--orange)] transition-colors">
                  <c.icon className="w-7 h-7 text-[var(--orange)] group-hover:text-white transition-colors" strokeWidth={2}/>
                </div>
                <div className="font-display font-bold text-[20px] mb-3">{c.t}</div>
                <p className="text-[14px] leading-[1.8] text-[var(--text-mid)]">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open positions block kept clean — no fabricated listings */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream-deep)]">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 reveal">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Current Openings</div>
              <h2 className="font-display text-[clamp(30px,3.8vw,56px)] leading-[1.05] font-bold tracking-tight">We're always <span className="font-serif-it text-[var(--orange)]">listening.</span></h2>
              <p className="mt-6 text-[16px] text-[var(--text-mid)] leading-[1.8]">Open positions are shared directly with shortlisted candidates. If you believe your experience aligns with EPC, solar, substations, or energy management work — we'd love to hear from you.</p>
            </div>

            <div className="lg:col-span-7 reveal">
              <div className="bg-white border border-[var(--line)] rounded-3xl p-8 md:p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[var(--orange)]/10 flex items-center justify-center shrink-0"><Mail className="w-6 h-6 text-[var(--orange)]"/></div>
                  <div>
                    <div className="font-display font-bold text-[20px]">Send us your CV</div>
                    <div className="text-[14px] text-[var(--text-mid)] mt-1">Share your resume and a brief note on the kind of role you're seeking.</div>
                  </div>
                </div>
                <a href={`mailto:${COMPANY.email}?subject=Career%20Application`} className="group flex items-center justify-between p-5 rounded-xl bg-[var(--cream)] border border-[var(--line)] hover:border-[var(--orange)] transition-colors">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)]">Apply via email</div>
                    <div className="font-display font-semibold text-[18px] text-[var(--orange)] mt-1">{COMPANY.email}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[var(--ink)] group-hover:rotate-45 transition-transform"/>
                </a>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <a href={`tel:${COMPANY.phoneTel}`} className="p-4 rounded-xl border border-[var(--line)] hover:border-[var(--ink)] transition-colors">
                    <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)]">Or call us</div>
                    <div className="font-display font-semibold mt-1">{COMPANY.phonePrimary}</div>
                  </a>
                  <a href={COMPANY.linkedin} target="_blank" rel="noreferrer" className="p-4 rounded-xl border border-[var(--line)] hover:border-[var(--ink)] transition-colors">
                    <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)]">Connect on</div>
                    <div className="font-display font-semibold mt-1">LinkedIn →</div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
